import { faCaretDown, faCaretUp } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';

import { FontAwesomeIcon } from 'atoms/icons';
import { LinkBox, LinkOther } from 'atoms/navigation/Box';
import { laptopAndAbove } from 'style/mediaQuery';

const Header = styled.header`
  display: none;
  @media ${laptopAndAbove} {
    display: grid;
    grid-template-columns: var(--columns);
    grid-template-areas: var(--areas);
    grid-gap: var(--unit);
    padding: var(--unit);
    row-gap: 0;
  }
`;
export const Line = styled(LinkBox)`
  display: grid;
  align-items: center;
  grid-template-columns: var(--columns);
  grid-template-areas: var(--areas);
  column-gap: var(--unit);
  row-gap: var(--half-unit);
  overflow: auto;
  padding: var(--double-unit) 0;
  border-bottom: 1px solid var(--c-nd-100);

  @media ${laptopAndAbove} {
    overflow: visible;
    &:hover,
    &:focus-within {
      border-color: transparent;
      &::before {
        position: absolute;
        inset: 0 calc(var(--unit) * -1);
        border-radius: var(--double-unit);
        background: var(--c-nd-100);
        content: '';
        z-index: -1;
      }
    }
  }
`;
type Cell = {
  area: string;
  hideOnDesktop?: boolean;
  showOnDesktop?: boolean;
  align?: 'right' | 'left';
};
export const Cell = styled.article<Cell>`
  grid-area: ${({ area }) => area};
  display: ${({ showOnDesktop }) => (showOnDesktop ? 'none' : 'flex')};
  align-items: center;
  justify-content: ${({ align = 'left' }) => align};
  gap: var(--half-unit);
  @media ${laptopAndAbove} {
    display: ${({ hideOnDesktop }) => (hideOnDesktop ? 'none' : 'flex')};
  }
`;

export const CellLink = styled(LinkOther)`
  &:focus,
  &:hover {
    color: inherit;
    &::before {
      background-color: var(--c-nd-100);
    }
  }
`;

const alignToFlex = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

type SortableHeaderType = {
  hideIcon?: boolean;
  align: 'left' | 'center' | 'right';
  sortable: boolean;
};
const SortableHeader = styled.div<SortableHeaderType>`
  display: flex;
  gap: var(--half-unit);
  align-items: center;
  width: 100%;
  padding: var(--unit) 0;
  font: var(--t-12);
  font-weight: var(--t-bold);
  color: var(--c-nd-800);
  height: 100%;
  justify-content: ${({ align }) => alignToFlex[align]};
  ${({ sortable, hideIcon }) =>
    sortable &&
    css`
      cursor: pointer;
      &:focus-visible {
        text-decoration: underline;
      }
      svg {
        opacity: ${hideIcon ? 0 : 1};
      }
      &:hover,
      &:focus-visible {
        svg {
          opacity: ${hideIcon ? 0.5 : 1};
        }
      }
    `}
`;

type TableHeader = {
  id: string;
  cell?: string;
  title?: string;
  align?: 'left' | 'center' | 'right';
}[];
type Props = {
  defaultSortBy?: { id: string; descending: boolean };
  rows: {
    header: TableHeader;
    body: ReactNode;
  };
  onSortChange?: (sortBy?: { id: string; descending: boolean }) => void;
};
export const Table = ({
  defaultSortBy,
  rows,
  onSortChange,
  ...rest
}: Props) => {
  const { header, body } = rows;
  const [sortBy, setSortBy] = useState(defaultSortBy);

  return (
    <section {...rest}>
      <Header>
        {header.map(({ cell, title, id, align = 'left' }) => {
          const isCurrentFilter = id === sortBy?.id;
          const descending = sortBy?.descending;
          return (
            <article key={id}>
              {!!cell && (
                <SortableHeader
                  {...(onSortChange
                    ? { sortable: true, as: 'button', type: 'button' }
                    : { sortable: false })}
                  hideIcon={!isCurrentFilter}
                  title={title}
                  align={align}
                  onClick={() => {
                    if (!onSortChange) {
                      return;
                    }
                    const sortByValue = {
                      id,
                      descending: !(isCurrentFilter && descending),
                    };
                    setSortBy(sortByValue);
                    onSortChange(sortByValue);
                  }}
                >
                  {cell}
                  {!!onSortChange && (
                    <FontAwesomeIcon
                      icon={
                        !isCurrentFilter || descending ? faCaretDown : faCaretUp
                      }
                    />
                  )}
                </SortableHeader>
              )}
            </article>
          );
        })}
      </Header>
      {body}
    </section>
  );
};

export type SortableTableHeader<T> = (TableHeader[number] & {
  accessor?: (item: T) => string;
})[];
